import { Component, computed, effect, inject, input, OnInit, signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatChipSet } from '@angular/material/chips';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { cloneDeep, isEqual, omit, xorWith } from 'lodash';
import { filter, Subject, switchMap, take, tap } from 'rxjs';
import {
  EFieldTypes,
  ESqlOperators,
  IApiDropdownFilterSave,
  IPredefinedFilterSave,
  TFilterSave,
  DateUtilitiesService as WidgetDateUtilities,
} from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../app.config';
import { dashboardContentActions } from '../../../state/dashboard-content/dashboard-content.actions';
import { AdvancedFilterInputsToggleChipComponent } from './advanced-filter-inputs-toggle-chip/advanced-filter-inputs-toggle-chip.component';
import { AdvancedFilterItemComponent } from './advanced-filter-item/advanced-filter-item.component';
import { AdvancedFilterManager } from './advanced-filter-manager';
import { AdvancedFilterPredefinedData } from './advanced-filter-predefined-data';
import {
  IAdvancedFilterApiValueConfig,
  IAdvancedFilterPredefinedConfig,
  TFilterSaveWithOnHold,
  TSelectedFilter,
} from './advanced-filter.interface';
import { AdvancedFilterService } from './advanced-filter.service';

@Component({
  imports: [AdvancedFilterItemComponent, MatChipSet, AdvancedFilterInputsToggleChipComponent, TranslatePipe, MatButton],
  providers: [AdvancedFilterService],
  selector: 'app-advanced-filter',
  styleUrl: './advanced-filter.component.scss',
  templateUrl: './advanced-filter.component.html',
})
export class AdvancedFilterComponent implements OnInit {
  private readonly advancedFilterPredefinedData = inject(AdvancedFilterPredefinedData);
  private readonly advancedFilterService = inject(AdvancedFilterService);
  private readonly widgetDateUtilities = inject(WidgetDateUtilities);
  private readonly store: Store<TReducerState> = inject(Store<TReducerState>);

  public readonly manager = input.required<AdvancedFilterManager>();
  public readonly apiFilters = computed(() => {
    return this.manager()
      .unappliedFilters()
      .reduce(
        (reducePayload, existingFilter) => {
          if (
            existingFilter.type === EFieldTypes.apiDropdown &&
            existingFilter.operator === ESqlOperators.$eq &&
            !existingFilter.isInvalid()
          ) {
            reducePayload[existingFilter.field] = existingFilter.value;
          }

          return reducePayload;
        },
        {} as Record<string, number>,
      );
  });

  public readonly visibleChips = computed(() => {
    return this.manager()
      .unappliedFilters()
      .filter(({ isOnHold }) => !isOnHold);
  });

  public readonly areAllApiDropdownReady = computed(() => {
    return this.manager()
      .unappliedFilters()
      .every(({ isInvalid }) => {
        return isInvalid?.() !== null;
      });
  });
  public readonly areAllApiDropdownReady$ = toObservable(this.areAllApiDropdownReady);

  private readonly lastAppliedFilters = signal<TSelectedFilter[]>([]);
  public readonly oneEditOperationAtATimeSubject = new Subject<symbol>();
  public readonly isApplyButtonShown = computed(() => {
    return (
      this.areAllApiDropdownReady() &&
      Boolean(
        xorWith(
          this.manager()
            .unappliedFilters()
            .map(({ field, operator, value, idOfCustom }) => ({
              field: idOfCustom ?? field,
              operator,
              value: Array.isArray(value) ? cloneDeep(value).sort() : value,
            })),
          this.lastAppliedFilters().map(({ field, operator, value, idOfCustom }) => ({
            field: idOfCustom ?? field,
            operator,
            value: Array.isArray(value) ? cloneDeep(value).sort() : value,
          })),
          isEqual,
        ).length,
      )
    );
  });
  public readonly isDashboardAdmin = this.store.selectSignal(
    ({ dashboardContent }) => dashboardContent.dashboardData?.isCurrentUserAdmin,
  );
  public readonly isSetAsDefaultButtonShown = computed(() => this.isApplyButtonShown() && this.isDashboardAdmin());

  constructor() {
    effect(() => {
      this.manager().unappliedFilters();
      untracked(() => this.manager().hoveredFilterIdentity.set(null));
    });

    effect(() => {
      const { fallbackDates, setDate } = this.manager().sqcdpDate();

      if (setDate !== -1) {
        return;
      }

      this.removeAllFiltersByField('date');

      if (fallbackDates !== null) {
        this.addFilter(
          this.widgetDateUtilities.getPredefinedEquivalentOfDatesAsFilterAdd(
            fallbackDates.startDate.format('YYYY-MM-DD'),
            fallbackDates.endDate.format('YYYY-MM-DD'),
            false,
          ),
        );
      }

      this.manager().sqcdpDate.set({ setDate: null, fallbackDates: null });
      this.apply();
    });

    effect(() => {
      const { setDate } = this.manager().sqcdpDate();

      if (setDate === null || setDate === -1) {
        return;
      }

      untracked(() => {
        this.removeAllFiltersByField('date');
        this.addFilter({
          field: 'date',
          id: Symbol(),
          isOnHold: false,
          operator: ESqlOperators.$between,
          value: {
            endDate: setDate,
            startDate: setDate,
          },
        });
        this.apply(false, true);
      });
    });
  }

  public ngOnInit(): void {
    this.autoApplySetAsDefault();
  }

  public removeFilter(filterToRemove: TSelectedFilter): void {
    this.manager().unappliedFilters.update((filters) =>
      filters.filter((existingFilter) => !isEqual(existingFilter, filterToRemove)),
    );
  }

  public apply(setAsDefault = false, isSetBySqcdpWheel = false): void {
    this.oneEditOperationAtATimeSubject.next(Symbol());
    this.lastAppliedFilters.set(this.manager().unappliedFilters());
    const validFilters = this.manager().unappliedValidFilters();
    this.manager().appliedFilters.set(validFilters);

    if (!isSetBySqcdpWheel) {
      this.manager().dateIsSetManuallySubject.next();
    }

    if (setAsDefault) {
      this.store.dispatch(dashboardContentActions.updateDefaultFilters({ defaultFilterCard: validFilters }));
    }
  }

  public addFilter(addOutput: TFilterSaveWithOnHold): void {
    const existingFilter = this.manager()
      .unappliedFilters()
      .find(({ id }) => id === addOutput.id);

    if (existingFilter) {
      this.updateChip(existingFilter, addOutput);
    } else {
      this.manager().unappliedFilters.update((filters) => {
        const newFilter = this.addTypeDataIntoFilterSave(addOutput);

        return [...filters, ...(newFilter ? [newFilter] : [])];
      });
    }
  }

  public updateChip(oldData: TSelectedFilter, newData: TFilterSave): void {
    this.manager().unappliedFilters.update((filters) =>
      filters.map((existingFilter) => {
        const isOldFilter =
          existingFilter.field === oldData.field &&
          existingFilter.operator === oldData.operator &&
          existingFilter.value === oldData.value;

        return isOldFilter ? this.addTypeDataIntoFilterSave({ ...newData, id: oldData.id })! : existingFilter;
      }),
    );
  }

  public makeOnHoldChipVisible(id: symbol): void {
    this.manager().unappliedFilters.update((filters) =>
      filters.map((unappliedFilter) =>
        unappliedFilter.id === id ? (omit(unappliedFilter, ['isOnHold']) as TSelectedFilter) : unappliedFilter,
      ),
    );
  }

  public deleteOnHoldChip(id: symbol): void {
    this.manager().unappliedFilters.update((filters) =>
      filters.filter((unappliedFilter) => unappliedFilter.id !== id || !unappliedFilter.isOnHold),
    );
  }

  private addTypeDataIntoFilterSave(addOutput: TFilterSaveWithOnHold): TSelectedFilter | null {
    const matchingConfiguration = this.advancedFilterService.extractConfiguration(addOutput);

    if (!matchingConfiguration) {
      return null;
    }

    let newFilter = {
      ...addOutput,
      type: matchingConfiguration.type,
    } as TSelectedFilter;

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (matchingConfiguration.type) {
      case EFieldTypes.apiDropdown:
        if (addOutput.operator !== ESqlOperators.$in) {
          newFilter = {
            ...(addOutput as IApiDropdownFilterSave),
            id: addOutput.id,
            isInvalid: signal(null),
            matcherName: (matchingConfiguration as IAdvancedFilterApiValueConfig).matcherName,
            type: matchingConfiguration.type,
          };
        }

        break;
      case EFieldTypes.predefined:
        if (addOutput.operator !== ESqlOperators.$in) {
          const matchingPredefinedValue = this.advancedFilterPredefinedData
            .getOption((matchingConfiguration as IAdvancedFilterPredefinedConfig).dataKey)
            .find(({ id }) => id === addOutput.value)!;

          newFilter = {
            ...(addOutput as IPredefinedFilterSave),
            id: addOutput.id,
            translatedName: matchingPredefinedValue.name,
            type: matchingConfiguration.type,
          };
        }

        break;
    }

    return newFilter;
  }

  private autoApplySetAsDefault(): void {
    this.manager()
      .areDefaultValuesReady$.pipe(
        filter(Boolean),
        take(1),
        tap(() => {
          for (const defaultValue of this.manager().defaultValues()) {
            this.addFilter({ ...defaultValue, id: Symbol() });
          }
        }),
        switchMap(() => this.areAllApiDropdownReady$.pipe(filter(Boolean))),
        take(1),
      )
      .subscribe(() => {
        this.apply();
      });
  }

  private removeAllFiltersByField(field: string): void {
    this.manager().unappliedFilters.update((filters) =>
      filters.filter((existingFilter) => existingFilter.field !== field),
    );
  }
}
